'use client';

import { useEffect, useRef, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../modal';
import { mountBlPlaceholder, registerBlPlaceholder, unmountBlPlaceholder } from './bl-mount';
import type { CardReserve } from './types';

type Props = {
	reserve: CardReserve;
	close: () => void;
	translations?: Record<string, string>;
};

export default function OpenTableModal({
	reserve,
	close,
	translations,
}: Props): ReactElement | null {
	const placeholderRef = useRef<HTMLDivElement>(null);
	const { modalId, widget } = reserve;

	useEffect(() => {
		const element = placeholderRef.current;
		registerBlPlaceholder(element);
		mountBlPlaceholder(element);
		return () => unmountBlPlaceholder(element);
	}, [modalId]);

	if (typeof document === 'undefined') return null;

	return createPortal(
		<div className="tailwind-root">
			<Modal
				close={close}
				mobileFullScreen
				focusContainerClassNames="max-w-[830px] md:h-auto max-h-[95vh]"
				modalPaddingClassNames=""
				contentContainerClassNames="w-full justify-start"
			>
				<div
					ref={placeholderRef}
					id={modalId}
					className="react-data OpenTable OpenTable-react-ssr-placeholder lazy-render"
					data-component-name="OpenTable"
					data-id={widget.id}
					data-modal-id={modalId}
					data-name={widget.name}
					data-en-name={widget.enName}
					data-address={widget.address}
					data-image={widget.image}
					data-occasions={JSON.stringify(widget.occasions ?? [])}
					data-alternate-availability-ids={JSON.stringify(widget.alternateAvailabilityIds ?? [])}
					data-disable-alternate-availability={String(widget.disableAlternateAvailability ?? false)}
					data-property-ors-code={widget.orsCode}
					data-property-name={widget.channel}
					data-property-region={widget.enRegion}
					data-property-subregion={widget.enCountry}
					data-stripe-public-key={widget.stripePublicKey}
					data-analytics-enabled={String(widget.analyticsEnabled ?? true)}
					data-launcher-mode={String(widget.launcherMode ?? false)}
					data-disable-prepopulating={String(widget.disablePrepopulating ?? false)}
					data-stores={JSON.stringify(widget.stores ?? [])}
					data-translations-string={JSON.stringify(translations ?? widget.translations ?? {})}
					data-schedule={JSON.stringify(widget.schedule ?? [])}
				/>
			</Modal>
		</div>,
		document.body
	);
}
